import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Image as ImageIcon, Pencil } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import SocialLinks from '../components/SocialLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Make a relative API path absolute so social crawlers can fetch it
function imageDisplayUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
}

// Always produce a fully-qualified URL for og:image / twitter:image
function absoluteImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  // Site-relative — prepend production host
  return `https://mddreminder.uk${url.startsWith('/') ? '' : '/'}${url}`;
}

// Extract YouTube video ID from any URL format (watch, youtu.be, shorts, embed, live)
// Returns null if not a recognisable YouTube URL.
function youtubeEmbedUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([A-Za-z0-9_-]{6,})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/,
    /(?:youtube\.com\/live\/)([A-Za-z0-9_-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

// Check if the content already has a [image:hero] block (so we don't double-render)
function hasInlineHero(blocks) {
  if (!Array.isArray(blocks)) return false;
  return blocks.some(b => b?.type === 'image' && b?.key === 'hero');
}

/**
 * Auto-inject mid1 / mid2 / bottom images into the content stream at sensible
 * positions if the article doesn't already contain explicit [image:*] markers.
 *
 * Manual posts often just have text blocks — the uploaded images would sit
 * in the DB unused. This makes them "just work" without needing the admin to
 * paste [image:mid1] etc. into their prose.
 *
 * AI-generated posts (which include explicit markers) are untouched: an image
 * is only auto-injected if no block already references that key.
 */
function withAutoImages(blocks, images) {
  if (!Array.isArray(blocks) || !images) return blocks || [];
  const existingKeys = new Set(
    blocks.filter(b => b?.type === 'image' && b?.key).map(b => b.key)
  );

  const has = (k) => images[k]?.url && !existingKeys.has(k);
  const wantMid1 = has('mid1');
  const wantMid2 = has('mid2');
  const wantBottom = has('bottom');

  if (!wantMid1 && !wantMid2 && !wantBottom) return blocks;

  // Only count "flow" blocks (text / heading / list) when picking insertion
  // points so headings aren't split awkwardly from their paragraphs.
  const flowIndexes = blocks
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => b && ['text', 'paragraph', 'heading', 'list'].includes(b.type))
    .map(({ i }) => i);

  // Split points: after ~1/3 and ~2/3 of the flow blocks
  const thirdIdx = flowIndexes[Math.floor(flowIndexes.length / 3)] ?? null;
  const twoThirdsIdx = flowIndexes[Math.floor((flowIndexes.length * 2) / 3)] ?? null;

  const out = [];
  blocks.forEach((b, i) => {
    out.push(b);
    if (wantMid1 && i === thirdIdx) out.push({ type: 'image', key: 'mid1' });
    if (wantMid2 && i === twoThirdsIdx && twoThirdsIdx !== thirdIdx) out.push({ type: 'image', key: 'mid2' });
  });
  if (wantBottom) out.push({ type: 'image', key: 'bottom' });
  return out;
}

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fintech-notify-check/artifacts/2hk0onm3_My%20Direct%20debit%20reminder%20app%20logo.png";
const ADMIN_EMAILS = ["diane.shawe@yahoo.com", "admin@mydirectdebits.com", "support@qualiquiz.app", "info@virtualautochat.uk"];

function BlogImage({ image, position }) {
  if (!image?.url) {
    return (
      <div className="my-8 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center min-h-[200px]" data-testid={`blog-image-placeholder-${position}`}>
        <ImageIcon className="w-10 h-10 text-gray-300 mb-2" />
        <p className="text-gray-400 text-sm">Image placeholder ({position})</p>
      </div>
    );
  }
  return (
    <figure className="my-8" data-testid={`blog-image-${position}`}>
      <img
        src={imageDisplayUrl(image.url)}
        alt={image.alt || ''}
        className="w-full rounded-2xl shadow-md"
        loading="lazy"
      />
      {image.alt && <figcaption className="text-center text-text-light text-xs mt-2">{image.alt}</figcaption>}
    </figure>
  );
}

function ContentBlock({ block, images }) {
  switch (block.type) {
    case 'text':
    case 'paragraph':
      return <p className="text-text-secondary leading-relaxed mb-4">{block.text}</p>;
    case 'heading':
      if (block.level === 2) return <h2 className="font-heading font-bold text-dark text-xl mt-10 mb-4">{block.text}</h2>;
      if (block.level === 3) return <h3 className="font-heading font-semibold text-dark text-lg mt-6 mb-3">{block.text}</h3>;
      return <h4 className="font-heading font-semibold text-dark mt-4 mb-2">{block.text}</h4>;
    case 'list':
      return (
        <ul className="list-disc pl-6 mb-4 space-y-1">
          {block.items.map((item, i) => <li key={i} className="text-text-secondary">{item}</li>)}
        </ul>
      );
    case 'image':
      return <BlogImage image={images?.[block.key]} position={block.key} />;
    default:
      return null;
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPosts, setAllPosts] = useState([]);

  const isAdmin = user && (ADMIN_EMAILS.includes(user.email?.toLowerCase()) || user.is_admin);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/blog/posts/${slug}`),
      axios.get(`${API_URL}/api/blog/posts`)
    ]).then(([postRes, postsRes]) => {
      setPost(postRes.data);
      setAllPosts(postsRes.data.filter(p => p.slug !== slug).slice(0, 3));
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-page flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-page flex flex-col items-center justify-center p-4">
      <p className="text-text-secondary mb-4">Post not found.</p>
      <Link to="/blog" className="text-primary font-semibold hover:underline">Back to Blog</Link>
    </div>
  );

  const faqSchema = post.faqs?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  } : null;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.meta_description,
    "url": `https://mddreminder.uk/blog/${post.slug}`,
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "author": { "@type": "Organization", "name": "Voliciti Group Ventures Ltd" },
    "publisher": { "@type": "Organization", "name": "Voliciti Group Ventures Ltd", "url": "https://mddreminder.uk" },
    ...(post.images?.hero?.url ? { "image": absoluteImageUrl(post.images.hero.url) } : {})
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mddreminder.uk" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://mddreminder.uk/blog" },
      { "@type": "ListItem", "position": 3, "name": post.title, "item": `https://mddreminder.uk/blog/${post.slug}` }
    ]
  };

  return (
    <div className="min-h-screen bg-page">
      <Helmet>
        <title>{post.meta_title || post.title}</title>
        <meta name="description" content={post.meta_description} />
        <link rel="canonical" href={`https://mddreminder.uk/blog/${post.slug}`} />
        {post.keywords?.length > 0 && <meta name="keywords" content={post.keywords.join(', ')} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description} />
        <meta property="og:url" content={`https://mddreminder.uk/blog/${post.slug}`} />
        {post.images?.hero?.url && <meta property="og:image" content={absoluteImageUrl(post.images.hero.url)} />}
        {post.images?.hero?.url && <meta property="og:image:width" content="1536" />}
        {post.images?.hero?.url && <meta property="og:image:height" content="1024" />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.meta_title || post.title} />
        <meta name="twitter:description" content={post.meta_description} />
        {post.images?.hero?.url && <meta name="twitter:image" content={absoluteImageUrl(post.images.hero.url)} />}
      </Helmet>

      <header className="glass-header px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Link to="/blog" className="text-text-secondary hover:text-primary transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="My Direct Debits" className="h-8 w-8" />
            <span className="font-heading font-semibold text-dark">MDD</span>
          </Link>
          <span className="text-text-light">|</span>
          <Link to="/blog" className="font-heading font-medium text-text-secondary hover:text-primary text-sm">Blog</Link>
          {isAdmin && (
            <Link to={`/admin/blog?edit=${post.slug}`} className="ml-auto text-primary hover:text-primary-dark" title="Edit post">
              <Pencil className="w-4 h-4" />
            </Link>
          )}
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="px-4 pt-6 pb-2 max-w-3xl mx-auto" aria-label="Breadcrumb">
        <ol className="flex flex-wrap gap-1 text-xs text-text-light">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li>/</li>
          <li><Link to="/blog" className="hover:text-primary">Blog</Link></li>
          <li>/</li>
          <li className="text-text-secondary truncate max-w-[200px]">{post.title}</li>
        </ol>
      </nav>

      <article className="px-4 pb-12">
        <div className="max-w-3xl mx-auto">
          <header className="mb-8">
            <h1 className="font-heading font-bold text-dark text-2xl sm:text-3xl lg:text-4xl leading-tight mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>
            {post.keywords?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.keywords.slice(0, 4).map(k => (
                  <span key={k} className="bg-primary-light text-primary-dark text-xs px-3 py-1 rounded-full font-medium">{k}</span>
                ))}
              </div>
            )}
            {/* Hero image auto-renders at the top (used to require manual [image:hero] tag) */}
            {post.images?.hero?.url && !hasInlineHero(post.content) && (
              <img
                src={imageDisplayUrl(post.images.hero.url)}
                alt={post.images.hero.alt || post.title}
                className="w-full rounded-2xl shadow-md object-cover"
                style={{ aspectRatio: '16 / 9' }}
                data-testid="blog-hero-image"
                loading="eager"
              />
            )}
          </header>

          {/* Blog Content — mid1/mid2/bottom images auto-injected at sensible
              positions if the article doesn't already reference them explicitly. */}
          <div className="prose prose-green max-w-none" data-testid="blog-post-content">
            {withAutoImages(post.content, post.images)?.map((block, i) => (
              <ContentBlock key={i} block={block} images={post.images} />
            ))}
          </div>

          {/* YouTube Video */}
          {(() => {
            const embedUrl = youtubeEmbedUrl(post.youtube_url);
            if (!embedUrl) return null;
            // Shorts are vertical (9:16) — detect and use a portrait aspect ratio so the video fits cleanly.
            const isShort = typeof post.youtube_url === 'string' && /youtube\.com\/shorts\//i.test(post.youtube_url);
            const aspectStyle = isShort
              ? { paddingBottom: '177.78%', maxWidth: '450px', margin: '0 auto' }
              : { paddingBottom: '56.25%' };
            return (
              <div className="my-10" data-testid="blog-youtube-embed">
                <div className="relative w-full rounded-2xl overflow-hidden shadow-lg" style={aspectStyle}>
                  <iframe
                    src={embedUrl}
                    title={post.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    loading="lazy"
                  />
                </div>
              </div>
            );
          })()}

          {/* FAQ Section */}
          {post.faqs?.length > 0 && (
            <section className="mt-12 pt-8 border-t border-border" data-testid="blog-faq-section">
              <h2 className="font-heading font-bold text-dark text-xl mb-6">Frequently Asked Questions</h2>
              <div className="space-y-5">
                {post.faqs.map((faq, i) => (
                  <div key={i} className="card-default p-5">
                    <h3 className="font-heading font-semibold text-dark text-base mb-2">{faq.question}</h3>
                    <p className="text-text-secondary text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          {(() => {
            const ctaTitle = post.cta_title || 'Take Control of Your Payments';
            const ctaDesc = post.cta_description || 'Start tracking your direct debits and subscriptions — free, simple, and no bank access required.';
            const ctaBtn = post.cta_button_text || 'Get Started Free';
            const ctaUrl = post.cta_url || '/auth';
            const isExternal = ctaUrl.startsWith('http');
            const ctaStyle = isExternal
              ? 'mt-12 bg-gradient-to-r from-dark to-primary-dark rounded-2xl p-8 text-center text-white'
              : 'mt-12 bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center';
            return (
              <section className={ctaStyle} data-testid="blog-cta">
                <h2 className={`font-heading font-bold text-xl mb-2 ${isExternal ? 'text-white' : 'text-dark'}`}>{ctaTitle}</h2>
                <p className={`mb-6 ${isExternal ? 'text-white/80' : 'text-text-secondary'}`}>{ctaDesc}</p>
                {isExternal ? (
                  <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-dark font-semibold px-8 py-3 rounded-xl text-base hover:bg-gray-100 transition-colors">
                    {ctaBtn}
                  </a>
                ) : (
                  <Link to={ctaUrl} className="btn-primary inline-block px-8 py-3 text-base font-semibold">
                    {ctaBtn}
                  </Link>
                )}
              </section>
            );
          })()}

          {/* Suggested quizzes (from AI generation) */}
          {Array.isArray(post.ai_meta?.suggested_quizzes) && post.ai_meta.suggested_quizzes.length > 0 && (
            <section className="mt-10" data-testid="suggested-quizzes">
              <h2 className="font-heading font-bold text-dark text-xl mb-3">Related quizzes</h2>
              <p className="text-text-secondary text-sm mb-4">A quick 60-second self-assessment if you'd like to dig deeper:</p>
              <ul className="space-y-2">
                {post.ai_meta.suggested_quizzes.map((q, i) => (
                  <li key={i}>
                    <a
                      href={q.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-default p-4 flex items-center justify-between gap-3 hover:border-primary transition-colors block"
                      data-testid={`suggested-quiz-${i}`}
                    >
                      <span className="text-sm font-medium text-dark">{q.title}</span>
                      <span className="text-primary text-xs flex-shrink-0">Take quiz →</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Related Posts */}
          {allPosts.length > 0 && (
            <section className="mt-12" data-testid="related-posts">
              <h2 className="font-heading font-bold text-dark text-xl mb-6">Related Articles</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {allPosts.map(p => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="card-default p-4 hover:shadow-md transition-shadow group">
                    <h3 className="font-heading font-semibold text-dark text-sm mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                    <p className="text-text-light text-xs line-clamp-2">{p.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <footer className="border-t border-border px-4 py-8 text-center">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-6 text-sm text-text-secondary">
          <Link to="/" className="hover:text-primary">Home</Link>
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-primary">Terms</Link>
          <Link to="/cookies" className="hover:text-primary">Cookies</Link>
        </div>
        <div className="mt-4">
          <SocialLinks />
        </div>
        <p className="text-text-light text-xs mt-4">&copy; 2026 Voliciti Group Ventures Ltd.</p>
      </footer>

      {/* Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
    </div>
  );
}
